import { queryClient, useApiClient } from '@m43/core';
import { QueueViewsResource } from '@mark43/cad-api';
import { useQuery } from '@tanstack/react-query';
import { runInAction } from 'mobx';
import { useIntegratedStreams } from '../common/hooks/use-integrated-streams';
import { useUnitQueueViews } from './unit-queue-views-provider';

export function useIntegratedUnitQueueViews() {
    const { unitQueueViewsRef, getListeners } = useUnitQueueViews();
    const apiClient = useApiClient();

    const unitQueueViewsBootstrapQuery = useQuery({
        queryKey: ['unit-queue-views-bootstrap'],
        queryFn() {
            return apiClient.request<QueueViewsResource.BootstrapUnitQueueViews>({
                method: 'POST',
                url: 'queue_views/unit_queue_views/bootstrap/',
                data: {
                    agencyTypeGlobalAttrIds: [],
                    dispatchAreaIds: [],
                    queueColumnFilters: [],
                    isPinToPrimaryActive: true,
                    unitTypeAttrIds: [],
                    isCadUnitLevelDispositionsEnabled: false,
                },
            });
        },
        onSuccess(resp) {
            const unitQueueViews = resp.data.data;
            if (unitQueueViews) {
                unitQueueViewsRef.current = Object.fromEntries(
                    unitQueueViews.map((unitQueueView) => [unitQueueView.id, unitQueueView])
                );
                getListeners?.().forEach((listener) => listener.onBootstrap());
            }
        },
    });

    useIntegratedStreams({
        query: unitQueueViewsBootstrapQuery,
        streamNames: ['unitQueues'],
        onMessage(message) {
            message.payload.unitQueueViews.forEach((unitQueueView) => {
                unitQueueViewsRef.current[unitQueueView.id] = unitQueueView;
            });
            message.payload.offDutyUnitIds.forEach((id) => {
                delete unitQueueViewsRef.current[id];
            });
            runInAction(() => {
                getListeners?.().forEach((listener) => listener.onUpdate(message.payload));
            });
        },
        onReconnect(socketStatusChangeEvent) {
            const messagePosition =
                socketStatusChangeEvent.streamsLastMessageMapBeforeSocketClosure?.['unitQueues']
                    ?.messagePosition;
            if (messagePosition) {
                return apiClient
                    .request<QueueViewsResource.CatchupUnitQueueViews>({
                        method: 'POST',
                        url: 'queue_views/unit_queue_views/catchup/',
                        data: {
                            offset: messagePosition,
                        },
                    })
                    .then((response) => {
                        const shouldBootstrap = response.data.data?.shouldBootstrap;
                        const bundles = response.data.data?.orderedUnitQueueBundles;
                        if (shouldBootstrap) {
                            unitQueueViewsBootstrapQuery.refetch();
                        } else if (bundles) {
                            bundles.forEach((bundle) => {
                                bundle.unitQueueViews.forEach((unitQueueView) => {
                                    unitQueueViewsRef.current[unitQueueView.id] = unitQueueView;
                                });
                                bundle.offDutyUnitIds?.forEach((id) => {
                                    delete unitQueueViewsRef.current[id];
                                });
                            });
                            getListeners?.().forEach((listener) => listener.onBootstrap());
                        }
                    })
                    .catch(() => {
                        queryClient.invalidateQueries(['unit-queue-views-bootstrap']);
                    });
            } else {
                return unitQueueViewsBootstrapQuery.refetch();
            }
        },
    });
}
